import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { ReportAPIResponse } from '../../ReportsBeta/api';
import { InvestmentSplitWithAnswers } from './utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faCircleXmark,
  faArrowUpRightFromSquare,
} from '@fortawesome/pro-light-svg-icons';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  createNewReport,
  SuccessResponse,
} from '../../ReportsBeta/NewReport/api';
import { useClient } from 'shared/apollo';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { AllocationRequestResponse } from './api';
import { getProgramNameUrl } from '../../ReportBuilder/Components/Budget/utils';

interface AllocationAllDoneProps {
  totalInvestments: number;
  totalFinishedInvestments: number;
  reportId: number;
  investment: Required<InvestmentSplitWithAnswers>;
  allocatingRequestState: RequestState<AllocationRequestResponse>;
  isImpactSnapshot?: boolean;
}

const StyledDiv = styled.div`
  margin-top: ${SPACERS.small};
  .allocation-all-done {
    margin-top: ${SPACERS.tiny};
    margin-bottom: ${SPACERS.tiny};
  }
  .investment-done-icon {
    width: 48px;
    height: 48px;
  }
  .investment-report-button {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: ${SPACERS.medium};
    svg {
      margin-right: ${SPACERS.extraTiny};
    }
  }
`;

const AllocationAllDone: React.FC<AllocationAllDoneProps> = ({
  reportId,
  investment,
  totalInvestments,
  totalFinishedInvestments,
  isImpactSnapshot,
}) => {
  const client = useClient();
  const [newReport, makeNewReportRequest] = useGraphQLRequest<
    SuccessResponse<'newReport'>
  >();

  const handleGoToReport = useCallback(() => {
    const resolvedReportId =
      reportId ||
      (newReport.type === 'REQUEST_SUCCESS' && newReport.data.newReport.success
        ? newReport.data.newReport.id
        : undefined);
    if (!resolvedReportId) return;
    const url = isImpactSnapshot
      ? `/npo/programs-and-reports?programId=${investment.program.Id}&reportId=${resolvedReportId}`
      : (() => {
          const programName = getProgramNameUrl(investment.program.name);
          return `/npo/report-builder/${programName}/${resolvedReportId}`;
        })();
    window.open(url, '_blank');
  }, [newReport, reportId, investment.program, isImpactSnapshot]);

  const handleCreateNewReport = useCallback(() => {
    client &&
      makeNewReportRequest(
        createNewReport(client, investment.program, investment.year),
      );
  }, [client, investment]);

  useEffect(() => {
    if (reportId) return;
    if (newReport.type === 'REQUEST_INIT') {
      handleCreateNewReport();
    }
  }, [reportId, newReport.type]);

  const numberOfInvestmentsLeft = totalInvestments - totalFinishedInvestments;

  return (
    <StyledDiv>
      <FontAwesomeIcon
        color={COLORS.success}
        icon={faCheck}
        className="investment-done-icon"
      />
      <TrueImpactText
        variant="heading-subheading"
        className="allocation-all-done"
      >
        This allocation is completed!
      </TrueImpactText>
      {numberOfInvestmentsLeft ? (
        <TrueImpactText>
          You have {numberOfInvestmentsLeft} left to complete.
        </TrueImpactText>
      ) : null}
      <div className="investment-report-button">
        {reportId ? (
          <TrueImpactButton
            onClick={handleGoToReport}
            variant="secondary-large"
          >
            <FontAwesomeIcon
              color={COLORS.success}
              icon={faArrowUpRightFromSquare}
            />{' '}
            Go to this report
          </TrueImpactButton>
        ) : (
          <TrueImpactButton
            variant="secondary-large"
            isLoading={newReport.type === 'REQUEST_START'}
            onClick={
              newReport.type === 'REQUEST_SUCCESS'
                ? handleGoToReport
                : handleCreateNewReport
            }
          >
            {newReport.type === 'REQUEST_INIT' ? (
              'Create new report for this investment'
            ) : newReport.type === 'REQUEST_SUCCESS' ? (
              <>
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  color={COLORS.success}
                />{' '}
                Go to the Report
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCircleXmark} color={COLORS.critical} />{' '}
                Unable to create this report. Try again!
              </>
            )}
          </TrueImpactButton>
        )}
      </div>
    </StyledDiv>
  );
};

export default AllocationAllDone;
